/**
 * Excel Service
 * 
 * Provides Excel (XLSX) import and export functionality using SheetJS.
 * Supports:
 * - Exporting data to multi-sheet Excel workbooks
 * - Importing data from Excel files with column mapping
 * - Format helpers for headers, column widths, etc.
 */

import * as XLSX from 'xlsx';

// ============================================================================
// Types
// ============================================================================

export interface ExcelSheet {
  name: string;
  data: Record<string, unknown>[];
  columns?: ExcelColumnConfig[];
}

export interface ExcelColumnConfig {
  key: string;
  header: string;
  width?: number;
  type?: 'string' | 'number' | 'date' | 'boolean';
}

export interface ExcelExportOptions {
  filename: string;
  sheets: ExcelSheet[];
  includeHeaders?: boolean;
}

export interface ExcelImportResult {
  success: boolean;
  sheets: {
    name: string;
    data: Record<string, unknown>[];
    headers: string[];
  }[];
  error?: string;
}

export interface ColumnMapping {
  excelColumn: string;
  appField: string;
  transform?: (value: unknown) => unknown;
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Export data to an Excel file with multiple sheets
 */
export function exportToExcel(options: ExcelExportOptions): void {
  const { filename, sheets, includeHeaders = true } = options;
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  for (const sheet of sheets) {
    // Create worksheet from data
    const worksheet = createWorksheet(sheet.data, sheet.columns, includeHeaders);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
  }
  
  // Generate and download the file
  XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

/**
 * Export a single sheet of data to Excel
 */
export function exportSingleSheet(
  data: Record<string, unknown>[],
  filename: string,
  sheetName: string = 'Sheet1',
  columns?: ExcelColumnConfig[]
): void {
  exportToExcel({
    filename,
    sheets: [{ name: sheetName, data, columns }],
  });
}

/**
 * Create a worksheet from data with optional column configuration
 */
function createWorksheet(
  data: Record<string, unknown>[],
  columns?: ExcelColumnConfig[],
  includeHeaders: boolean = true
): XLSX.WorkSheet {
  if (data.length === 0) {
    return XLSX.utils.aoa_to_sheet([['No data']]);
  }
  
  // Determine column order and headers
  const columnKeys = columns 
    ? columns.map(c => c.key) 
    : Object.keys(data[0]);
  
  const headers = columns 
    ? columns.map(c => c.header || c.key)
    : columnKeys;
  
  // Build the data array
  const rows: unknown[][] = [];
  
  if (includeHeaders) {
    rows.push(headers);
  }
  
  for (const item of data) {
    const row = columnKeys.map(key => {
      const value = item[key];
      // Handle dates
      if (value instanceof Date) {
        return value.toISOString().split('T')[0];
      }
      // Handle undefined/null
      if (value === undefined || value === null) {
        return '';
      }
      return value;
    });
    rows.push(row);
  }
  
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  
  // Apply column widths if specified
  if (columns) {
    const colWidths = columns.map(c => ({ wch: c.width || 15 }));
    worksheet['!cols'] = colWidths;
  }
  
  return worksheet;
}

// ============================================================================
// Import Functions
// ============================================================================

/**
 * Import data from an Excel file
 */
export async function importFromExcel(file: File): Promise<ExcelImportResult> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    const sheets = workbook.SheetNames.map(sheetName => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
        defval: '',
        raw: false,
      });
      
      // Extract headers from the first row
      const headers = worksheet['!ref'] 
        ? getHeadersFromWorksheet(worksheet)
        : [];
      
      return {
        name: sheetName,
        data: jsonData,
        headers,
      };
    });
    
    return {
      success: true,
      sheets,
    };
  } catch (error) {
    return {
      success: false,
      sheets: [],
      error: error instanceof Error ? error.message : 'Failed to parse Excel file',
    };
  }
}

/**
 * Extract header names from worksheet
 */
function getHeadersFromWorksheet(worksheet: XLSX.WorkSheet): string[] {
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const headers: string[] = [];
  
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cell = worksheet[cellAddress];
    headers.push(cell ? String(cell.v) : `Column ${col + 1}`);
  }
  
  return headers;
}

/**
 * Apply column mapping to imported data
 */
export function applyColumnMapping(
  data: Record<string, unknown>[],
  mappings: ColumnMapping[]
): Record<string, unknown>[] {
  return data.map(row => {
    const mappedRow: Record<string, unknown> = {};
    
    for (const mapping of mappings) {
      let value = row[mapping.excelColumn];
      
      if (mapping.transform) {
        value = mapping.transform(value);
      }
      
      mappedRow[mapping.appField] = value;
    }
    
    return mappedRow;
  });
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate that required columns exist in imported data
 */
export function validateRequiredColumns(
  headers: string[],
  requiredColumns: string[]
): { valid: boolean; missing: string[] } {
  const headerSet = new Set(headers.map(h => h.toLowerCase()));
  const missing = requiredColumns.filter(
    col => !headerSet.has(col.toLowerCase())
  );
  
  return {
    valid: missing.length === 0,
    missing,
  };
}

/**
 * Get suggested mappings based on column name similarity.
 * Checks exact key/label match first, then label partial match, then key partial
 * match. Custom fields (those with a key in the appFields list) participate
 * equally — no separate pass needed.
 */
export function suggestColumnMappings(
  excelHeaders: string[],
  appFields: { key: string; label: string }[]
): Record<string, string> {
  const suggestions: Record<string, string> = {};

  // Pre-normalise all field keys and labels once
  const normalised = appFields.map(f => ({
    key: f.key,
    normKey: f.key.toLowerCase().replace(/[_\s#-]/g, ''),
    normLabel: f.label.toLowerCase().replace(/[_\s#-]/g, ''),
  }));

  for (const header of excelHeaders) {
    const normHeader = header.toLowerCase().replace(/[_\s#-]/g, '');

    // 1. Exact match on key or label
    const exact = normalised.find(
      f => f.normKey === normHeader || f.normLabel === normHeader
    );
    if (exact) { suggestions[header] = exact.key; continue; }

    // 2. Label contains header or header contains label (longer string wins)
    const labelPartial = normalised.find(
      f => f.normLabel.includes(normHeader) || normHeader.includes(f.normLabel)
    );
    if (labelPartial) { suggestions[header] = labelPartial.key; continue; }

    // 3. Key partial match (fallback)
    const keyPartial = normalised.find(
      f => f.normKey.includes(normHeader) || normHeader.includes(f.normKey)
    );
    if (keyPartial) { suggestions[header] = keyPartial.key; }
  }

  return suggestions;
}

// ============================================================================
// Data Prep Functions
// ============================================================================

/**
 * Configuration produced by the DataPrepStep wizard. Controls how raw CSV/
 * Excel rows are cleaned before column mapping begins.
 */
export interface DataPrepConfig {
  /** Skip rows where every value is "0" or empty */
  skipZeroRows: boolean;
  /** Treat lone "0" values as null/empty across all columns */
  treatZeroAsNull: boolean;
  /** Column headers to coerce to boolean (x → true, 0 → false) */
  booleanColumns: string[];
  /** Auto-create workspace custom fields for detected boolean columns */
  autoCreateBooleanFields: boolean;
  /** Column identified as a section/grouping label (e.g. "Division") */
  sectionColumn: string | null;
  /** How to handle the section column */
  sectionColumnTreatment: 'tag' | 'custom_field' | 'skip';
}

function _normalizeCell(v: unknown): string {
  return String(v ?? '').trim();
}

function _isBlankOrZero(v: unknown): boolean {
  const s = _normalizeCell(v);
  return s === '' || s === '0';
}

function _detectDataIssues(headers: string[], rows: Record<string, unknown>[]) {
  if (rows.length === 0) {
    return { zeroRowCount: 0, zeroRatio: 0, booleanCols: [] as string[], sectionColumn: null as string | null, sectionValues: [] as string[] };
  }

  const zeroRowCount = rows.filter(row => Object.values(row).every(v => _isBlankOrZero(v))).length;

  const allVals = rows.flatMap(r => Object.values(r)).filter(v => _normalizeCell(v) !== '');
  const zeroCount = allVals.filter(v => _normalizeCell(v) === '0').length;
  const zeroRatio = allVals.length > 0 ? zeroCount / allVals.length : 0;

  const booleanCols: string[] = [];
  for (const header of headers) {
    const colVals = rows.map(r => _normalizeCell(r[header])).filter(v => v !== '');
    if (colVals.length < 3) continue;
    const boolLike = colVals.filter(v => v === 'x' || v === 'X' || v === '0' || v === '1').length;
    const hasX = colVals.some(v => v === 'x' || v === 'X');
    if (hasX && boolLike / colVals.length >= 0.8) booleanCols.push(header);
  }

  let sectionColumn: string | null = null;
  let sectionValues: string[] = [];
  const firstHeader = headers[0];
  if (firstHeader) {
    const nonBlank = rows.map(r => _normalizeCell(r[firstHeader])).filter(v => v !== '' && v !== '0');
    const unique = Array.from(new Set(nonBlank));
    if (unique.length >= 2 && unique.length <= 15 && nonBlank.length / unique.length >= 2) {
      sectionColumn = firstHeader;
      sectionValues = unique.slice(0, 6);
    }
  }

  return { zeroRowCount, zeroRatio, booleanCols, sectionColumn, sectionValues };
}

/**
 * Build a default DataPrepConfig by running heuristics on the parsed rows.
 * The wizard UI (DataPrepStep) allows the user to adjust these defaults.
 */
export function buildDefaultDataPrepConfig(
  headers: string[],
  rows: Record<string, unknown>[]
): DataPrepConfig {
  const d = _detectDataIssues(headers, rows);
  return {
    skipZeroRows: d.zeroRowCount > 0,
    treatZeroAsNull: d.zeroRatio > 0.2,
    booleanColumns: d.booleanCols,
    autoCreateBooleanFields: d.booleanCols.length > 0,
    sectionColumn: d.sectionColumn,
    sectionColumnTreatment: 'custom_field',
  };
}

/**
 * Apply a DataPrepConfig to raw parsed rows.
 *
 * Filtering and value normalisation happen here, before column mapping:
 *  - skipZeroRows:     remove rows where every value is blank / "0"
 *  - treatZeroAsNull:  replace lone "0" cells with empty string
 *  - booleanColumns:   coerce "x"/"X" → true, "0"/"" → false
 *  - sectionColumn / sectionColumnTreatment: handled here for 'tag' mode;
 *    'custom_field' and 'skip' are handled during the column-mapping step
 *    (the column is simply mapped or left unmapped by the user).
 */
export function applyDataPrepConfig(
  rows: Record<string, unknown>[],
  config: DataPrepConfig,
  allHeaders: string[]
): Record<string, unknown>[] {
  let result = rows;

  // 1. Filter zero-sentinel rows
  if (config.skipZeroRows) {
    result = result.filter(row =>
      !allHeaders.every(h => {
        const v = String(row[h] ?? '').trim();
        return v === '' || v === '0';
      })
    );
  }

  // 2. Normalise values
  const boolSet = new Set(config.booleanColumns);
  result = result.map(row => {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(row)) {
      let val = row[key];
      const str = String(val ?? '').trim();

      if (boolSet.has(key)) {
        // Coerce boolean columns
        val = str === 'x' || str === 'X' || str === '1' || str === 'true';
      } else if (config.treatZeroAsNull && str === '0') {
        val = '';
      }

      out[key] = val;
    }

    // Section column in 'tag' mode: append value to a __tags__ helper key
    if (
      config.sectionColumn &&
      config.sectionColumnTreatment === 'tag' &&
      row[config.sectionColumn]
    ) {
      const existing = String(out['__section_tag__'] ?? '');
      out['__section_tag__'] = existing
        ? `${existing},${row[config.sectionColumn]}`
        : String(row[config.sectionColumn]);
    }

    return out;
  });

  return result;
}

// ============================================================================
// Format Helpers
// ============================================================================

/**
 * Standard column configurations for common data types
 */
export const STANDARD_COLUMNS = {
  rank: { key: 'rank', header: 'Rank', width: 8, type: 'number' as const },
  title: { key: 'title', header: 'Title', width: 40, type: 'string' as const },
  description: { key: 'description', header: 'Description', width: 60, type: 'string' as const },
  score: { key: 'score', header: 'Score', width: 12, type: 'number' as const },
  status: { key: 'status', header: 'Status', width: 15, type: 'string' as const },
  aboveLine: { key: 'above_the_line', header: 'Above Line', width: 12, type: 'string' as const },
  createdAt: { key: 'created_at', header: 'Created', width: 12, type: 'date' as const },
  id: { key: 'id', header: 'ID', width: 20, type: 'string' as const },
};

/**
 * Create focus list export columns configuration
 */
export function getFocusListExportColumns(): ExcelColumnConfig[] {
  return [
    STANDARD_COLUMNS.rank,
    STANDARD_COLUMNS.title,
    STANDARD_COLUMNS.description,
    STANDARD_COLUMNS.score,
    STANDARD_COLUMNS.aboveLine,
    STANDARD_COLUMNS.status,
    STANDARD_COLUMNS.createdAt,
    STANDARD_COLUMNS.id,
  ];
}

/**
 * Create tool results export columns configuration
 */
export function getToolResultsExportColumns(
  criteriaNames: string[]
): ExcelColumnConfig[] {
  const baseColumns = [
    STANDARD_COLUMNS.rank,
    STANDARD_COLUMNS.title,
    STANDARD_COLUMNS.description,
  ];
  
  // Add criteria score columns
  const criteriaColumns = criteriaNames.map(name => ({
    key: `criteria_${name.toLowerCase().replace(/\s+/g, '_')}`,
    header: name,
    width: 12,
    type: 'number' as const,
  }));
  
  return [
    ...baseColumns,
    ...criteriaColumns,
    { key: 'total_score', header: 'Total Score', width: 12, type: 'number' as const },
    STANDARD_COLUMNS.id,
  ];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Parse a date string from Excel
 */
export function parseExcelDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    // Excel serial date
    return new Date((value - 25569) * 86400 * 1000);
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * Sanitize a string for use as a sheet name
 */
export function sanitizeSheetName(name: string): string {
  // Excel sheet names have restrictions
  return name
    .replace(/[\\/*?[\]]/g, '')
    .substring(0, 31);
}

export default {
  exportToExcel,
  exportSingleSheet,
  importFromExcel,
  applyColumnMapping,
  applyDataPrepConfig,
  buildDefaultDataPrepConfig,
  validateRequiredColumns,
  suggestColumnMappings,
  getFocusListExportColumns,
  getToolResultsExportColumns,
  parseExcelDate,
  sanitizeSheetName,
  STANDARD_COLUMNS,
};
